/**
 * フローパターン(shinsei_flow_spec v1.1 §2 の14パターン)。
 * 申請者役割 × 支払方法 × 支払者 → フロー型 を**設定駆動**にする:
 * Firestoreの flow_patterns コレクションを正とし、未投入・読込失敗時は
 * このファイルの既定値(仕様書§2と同一)にフォールバックする。
 * パターンの追加・変更はデータ変更(seed/コンソール)で行い、コードに直書きしない。
 *
 * フロー型とステップ(§3):
 *  normal(通常5): 申請→部門長確認→承認→支払(実績計上)→完了
 *  tatekae(立替7): 申請→確認→承認→立替支払(実績計上)→経理精算→受領(受領書添付)→完了
 *  kariuke(仮払い7): 申請→確認→承認→仮払い(経理・CF計上)→受領(受領書添付)→支払(実績計上)→完了
 * ステータス機構は共通(cash_ready=仮払い済み・received=受領済み、精算・受領はフィールドで記録)。
 */
import { collection, getDocs } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import type { FlowRole, Payer, PaymentMethod } from '@core/lib/types';

export type FlowType = 'normal' | 'tatekae' | 'kariuke';

export type FlowPattern = {
  id: string;
  /** 仕様書§2の番号(①〜⑭) */
  no: number;
  /** 適用される申請者役割。経理は管理側(部門長・承認者)と同じ扱い */
  roles: FlowRole[];
  payment_method: PaymentMethod;
  payer: Exclude<Payer, null>;
  flow_type: FlowType;
  note?: string;
};

const MGR: FlowRole[] = ['bucho', 'approver', 'accounting'];
const MEM: FlowRole[] = ['member'];

/** 既定値(=仕様書§2)。flow_patterns コレクションが空のときに使う */
export const DEFAULT_FLOW_PATTERNS: FlowPattern[] = [
  { id: 'p01', no: 1, roles: MEM, payment_method: 'cash', payer: 'accounting', flow_type: 'normal' },
  { id: 'p02', no: 2, roles: MEM, payment_method: 'bank_transfer', payer: 'accounting', flow_type: 'normal', note: '請求書必須' },
  { id: 'p03', no: 3, roles: MEM, payment_method: 'card', payer: 'self', flow_type: 'normal' },
  { id: 'p04', no: 4, roles: MEM, payment_method: 'card', payer: 'bucho', flow_type: 'normal' },
  { id: 'p05', no: 5, roles: MEM, payment_method: 'card', payer: 'accounting', flow_type: 'normal' },
  { id: 'p06', no: 6, roles: MEM, payment_method: 'reimbursement', payer: 'self', flow_type: 'tatekae' },
  { id: 'p07', no: 7, roles: MEM, payment_method: 'reimbursement', payer: 'bucho', flow_type: 'tatekae' },
  { id: 'p08', no: 8, roles: MGR, payment_method: 'cash', payer: 'accounting', flow_type: 'normal', note: '部門長確認なし' },
  { id: 'p09', no: 9, roles: MGR, payment_method: 'bank_transfer', payer: 'accounting', flow_type: 'normal', note: '請求書必須・部門長確認なし' },
  { id: 'p10', no: 10, roles: MGR, payment_method: 'card', payer: 'self', flow_type: 'normal', note: '部門長確認なし' },
  { id: 'p11', no: 11, roles: MGR, payment_method: 'card', payer: 'accounting', flow_type: 'normal', note: '部門長確認なし' },
  { id: 'p12', no: 12, roles: MGR, payment_method: 'reimbursement', payer: 'self', flow_type: 'tatekae', note: '部門長確認なし' },
  { id: 'p13', no: 13, roles: MEM, payment_method: 'prepaid', payer: 'self', flow_type: 'kariuke' },
  { id: 'p14', no: 14, roles: MGR, payment_method: 'prepaid', payer: 'self', flow_type: 'kariuke', note: '部門長確認なし' },
  // 2026-08-26 発注者指示による追加(仕様書§2の14パターンへの追加15番):
  // 承認者・経理は「立替×部門長」で申請できる。部門長がいない事業部(本部等)では
  // もう一方の承認者が部門長役として立替払いする(例: 後藤の申請を亀山が支払う)。
  // 部門長本人の立替は「自分」のみ(自分がその事業部の部門長のため)
  { id: 'p15', no: 15, roles: ['approver', 'accounting'], payment_method: 'reimbursement', payer: 'bucho', flow_type: 'tatekae', note: '部門長確認なし・もう一方の承認者等が立替' },
];

let cache: FlowPattern[] | null = null;

/** flow_patterns を読む(セッション内キャッシュ)。空・失敗時は既定値 */
export async function fetchFlowPatterns(): Promise<FlowPattern[]> {
  if (cache) return cache;
  try {
    const snap = await getDocs(collection(getDb(), 'flow_patterns'));
    const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as FlowPattern);
    cache = rows.length > 0 ? rows.sort((a, b) => a.no - b.no) : DEFAULT_FLOW_PATTERNS;
  } catch {
    cache = DEFAULT_FLOW_PATTERNS;
  }
  return cache;
}

/** 役割・支払方法・支払者に一致するパターン(なければ無効な組合せ) */
export function findPattern(
  patterns: FlowPattern[],
  role: FlowRole,
  method: PaymentMethod | '',
  payer: Payer,
): FlowPattern | null {
  if (!method || !payer) return null;
  return (
    patterns.find((p) => p.roles.includes(role) && p.payment_method === method && p.payer === payer) ?? null
  );
}

/** その役割・支払方法で選べる支払者(§2の有効な組合せのみ) */
export function payerOptionsFor(
  patterns: FlowPattern[],
  role: FlowRole,
  method: PaymentMethod | '',
): Exclude<Payer, null>[] {
  if (!method) return ['self', 'bucho', 'accounting'];
  const list = patterns.filter((p) => p.roles.includes(role) && p.payment_method === method).map((p) => p.payer);
  return [...new Set(list)];
}
